import { useState } from "react";
import { useIntl } from "react-intl";

// MUI Components
import { Box } from "@mui/material";

// Styles
import autocompleteFieldStyles from "../AutocompleteField.styles";

// Services
import { PersonWithWorkHistory, fetchPeopleDiscoverService } from "services";

// Utils
import { getPeopleParams, talentPotentialMembersPayloadColumns } from "services/people/people.functions";

const getOptionLabelPerson = (option: PersonWithWorkHistory | string) =>
    typeof option === "object" ? option.name : option ?? "";

const renderOptionPerson = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: PersonWithWorkHistory,
    classes: ReturnType<typeof autocompleteFieldStyles>
) => {
    const currentEmployment = option?.work_history?.find((wh) => wh?.is_current_employment);
    const companyName = currentEmployment?.company_name || currentEmployment?.unlinked_company;

    return (
        <li {...props} key={props.id}>
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                sx={{
                    padding: 0.1,
                    "& p": {
                        padding: 0,
                        margin: 0,
                        fontWeight: "bold",
                    },
                }}
            >
                <span style={{ textTransform: "capitalize" }}>{option.name}</span>
                <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
                    <Box order={1} pr={1}>
                        <p style={{ fontSize: 12 }}>
                            {currentEmployment?.drx_work_position_label || <strong>?</strong>}
                            {companyName && <span> @ {companyName}</span>}
                        </p>
                    </Box>
                </Box>
            </Box>
        </li>
    );
};

const useAutocompletePeople = (
    payloadColumns = talentPotentialMembersPayloadColumns
): {
    peopleList: PersonWithWorkHistory[];
    placeholderPerson: string;
    setPeopleList: React.Dispatch<React.SetStateAction<PersonWithWorkHistory[]>>;
    getOptionLabelPerson: typeof getOptionLabelPerson;
    renderOptionPerson: typeof renderOptionPerson;
    handleInputChangePerson: (needle: string) => Promise<PersonWithWorkHistory[] | undefined>;
} => {
    const intl = useIntl();
    const [peopleList, setPeopleList] = useState<PersonWithWorkHistory[]>([]);

    const placeholderPerson = intl.formatMessage({ id: "talent.people.placeholder" });

    const handleInputChangePerson = async (needle: string) => {
        if (needle.length < 2) return;

        const params = getPeopleParams({
            termField: "name",
            termFilter: needle,
            sort: { "name.keyword": "asc" },
            meta: { page_no: 1, page_size: 50 },
            columns: payloadColumns,
        });

        try {
            const {
                data: { results },
            } = await fetchPeopleDiscoverService<PersonWithWorkHistory>(params);

            setPeopleList(results);

            return results;
        } catch (error) {
            console.error(error);
        }
    };

    return {
        peopleList,
        placeholderPerson,
        setPeopleList,
        getOptionLabelPerson,
        renderOptionPerson,
        handleInputChangePerson,
    };
};

export default useAutocompletePeople;
